import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Tooltip,
  TableContainer
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Sync as SyncIcon,
  Storage as StorageIcon,
  Lan as PortIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../services/useApi';
import PortsModal from './PortsModal';
import OlTCardForm from '../../../components/forms/OlTCardForm';

interface Card {
  id: number;
  slot: number;
  model?: string;
  qtde_portas: number;
  serialnumber?: string;
  sw?: string;
  status?: string;
  description?: string;
  last_sync?: string;
  olt?: {
    id: number;
    name: string;
    vendor: string;
  };
}

const CardsTab: React.FC = () => {
  const { id: oltId } = useParams<{ id: string }>();
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Vendor da OLT (ex.: datacom) — usado para ocultar "Sincronizar Cards" em OLTs com apenas 1 card */
  const [oltVendor, setOltVendor] = useState<string | null>(null);

  // Estado do modal de portas
  const [portsModalOpen, setPortsModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);

  // Estado do modal de cadastro/edição de card
  const [cardFormOpen, setCardFormOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<Card | null>(null);

  const fetchCards = async () => {
    if (!oltId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/olts/${oltId}/cards`);

      if (response.data.success) {
        setCards(response.data.data);
        if (response.data.vendor != null) setOltVendor(String(response.data.vendor).toLowerCase());
      } else {
        setError('Erro ao carregar cards');
      }
    } catch (err: any) {
      console.error('Erro ao carregar cards:', err);
      setError('Erro ao carregar cards');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!oltId) return;

    setSyncing(true);
    try {
      const response = await api.post(`/olts/${oltId}/cards/sync`);
      if (response.data.success) {
        toast.success(response.data.message || 'Sincronização concluída com sucesso!');
        await fetchCards(); // Recarregar a lista
      } else {
        toast.error(response.data.message || 'Erro na sincronização');
      }
    } catch (err: any) {
      console.error('Erro ao sincronizar cards:', err);
      toast.error('Erro ao sincronizar cards');
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (cardId: number) => {
    if (!window.confirm('Deseja realmente deletar este card?')) return;

    try {
      const response = await api.delete(`/olts/cards/${cardId}`);
      if (response.data.success) {
        toast.success('Card deletado com sucesso!');
        setCards(cards.filter(card => card.id !== cardId));
      } else {
        toast.error(response.data.message || 'Erro ao deletar card');
      }
    } catch (err: any) {
      console.error('Erro ao deletar card:', err);
      toast.error('Erro ao deletar card');
    }
  };

  // Função para abrir modal de portas
  const handleOpenPortsModal = (card: Card) => {
    setSelectedCard(card);
    setPortsModalOpen(true);
  };

  // Função para fechar modal de portas
  const handleClosePortsModal = () => {
    setPortsModalOpen(false);
    setSelectedCard(null);
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'maintenance':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'active':
        return 'Ativo';
      case 'inactive':
        return 'Inativo';
      case 'maintenance':
        return 'Manutenção';
      default:
        return 'Desconhecido';
    }
  };

  useEffect(() => {
    fetchCards();
  }, [oltId]);

  return (
    <Box sx={{ p: 0.5 }}>
      {/* Cabeçalho */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <StorageIcon color="primary" />
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Cards da OLT
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => { setEditingCard(null); setCardFormOpen(true); }}
            color="primary"
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            Cadastrar
          </Button>
          {oltVendor !== 'datacom' && (
            <Button
              variant="contained"
              startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={handleSync}
              disabled={syncing}
              color="primary"
              size="small"
              sx={{ fontSize: '0.8rem' }}
            >
              {syncing ? 'Sincronizando...' : 'Sincronizar Cards'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Conteúdo */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress size={40} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }}>
          {error}
        </Alert>
      ) : (
        <TableContainer
            component={Paper}
            elevation={2}
            sx={{
              borderRadius: 2,
              overflowX: 'auto',
              mb: 3,
              border: 'none'
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: 800,
                '& .MuiTableBody-root .MuiTableCell-root': {
                  py: 0.6,
                  fontSize: '0.78rem'
                }
              }}
            >
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Slot</strong></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Modelo</strong></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Portas</strong></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Serial Number</strong></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Status</strong></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>Última Sincronização</strong></TableCell>
                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}><strong>Ações</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cards.length > 0 ? (
                cards.map((card) => (
                  <TableRow key={card.id} hover>
                    <TableCell>
                      <Chip
                        label={`Slot ${card.slot}`}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {card.model || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Clique para gerenciar portas">
                        <Chip
                          label={`${card.qtde_portas} portas`}
                          color="info"
                          variant="outlined"
                          size="small"
                          onClick={() => handleOpenPortsModal(card)}
                          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' }, fontSize: '0.7rem', height: 20 }}
                          icon={<PortIcon />}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                        {card.serialnumber || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(card.status)}
                        color={getStatusColor(card.status) as any}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </TableCell>
                    <TableCell>
                      {card.last_sync ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          {new Date(card.last_sync).toLocaleString('pt-BR')}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                          Nunca sincronizado
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar card">
                        <IconButton
                          color="primary"
                          onClick={() => { setEditingCard(card); setCardFormOpen(true); }}
                          size="small"
                          sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Deletar card">
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(card.id)}
                          size="small"
                          sx={{ p: 0.5, '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                      <StorageIcon color="action" sx={{ fontSize: 48 }} />
                      <Typography variant="body1" color="text.secondary">
                        Nenhum card encontrado
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {oltVendor === 'datacom'
                          ? 'OLT Datacom possui apenas 1 card (slot 1), criado automaticamente ao abrir.'
                          : 'Clique em "Cadastrar" para adicionar um card manualmente ou "Sincronizar Cards" para importar da OLT'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Informações adicionais */}
      {cards.length > 0 && (
        <Box mt={1.5}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            Total de cards: {cards.length} |
            Última atualização: {new Date().toLocaleString('pt-BR')}
          </Typography>
        </Box>
      )}

      {/* Modal de Portas */}
      <PortsModal
        open={portsModalOpen}
        onClose={handleClosePortsModal}
        selectedCard={selectedCard}
        oltId={oltId}
      />

      {/* Modal de Cadastro/Edição de Card */}
      <OlTCardForm
        open={cardFormOpen}
        onClose={() => { setCardFormOpen(false); setEditingCard(null); }}
        onSuccess={fetchCards}
        oltId={oltId || ''}
        card={editingCard}
      />
    </Box>
  );
};

export default CardsTab; 